import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Table, Alert, Badge, Button, Card, ListGroup, Collapse } from 'react-bootstrap';
import { FaPlus, FaTrash, FaEye, FaEyeSlash, FaExclamationTriangle } from 'react-icons/fa';

const CreateClassesStep = ({ workflowData, setWorkflowData, schoolid }) => {
  const [rawClassText, setRawClassText] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [existingClasses, setExistingClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [parseMode, setParseMode] = useState("newline");
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
  const [showExistingClasses, setShowExistingClasses] = useState(false);

  // Grade level options - matching your Class model with all grade levels
  const gradeLevels = [
    { value: 1, label: "Junior Infants" },
    { value: 2, label: "Senior Infants" },
    { value: 3, label: "1st Class" },
    { value: 4, label: "2nd Class" },
    { value: 5, label: "3rd Class" },
    { value: 6, label: "4th Class" },
    { value: 7, label: "5th Class" },
    { value: 8, label: "6th Class" },
    { value: 9, label: "7th Class" },
    { value: 10, label: "8th Class" }
  ];

  // Academic years - following the same pattern as GroupStudentTransfersModal
  const academicYears = [
    { value: "2026/27", label: "2026/27 Academic Year" },
    { value: "2025/26", label: "2025/26 Academic Year" },
    { value: "2024/25", label: "2024/25 Academic Year" },
    { value: "2023/24", label: "2023/24 Academic Year" },
    { value: "2022/23", label: "2022/23 Academic Year" }
  ];

  // Get default academic year based on current date
  const getDefaultAcademicYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const august1st = new Date(currentYear, 7, 1); // August 1st of current year (month is 0-indexed)
    
    if (now >= august1st) {
      // After August 1st, use current year as start of academic year
      return `${currentYear}/${(currentYear + 1).toString().slice(-2)}`;
    } else {
      // Before August 1st, use previous year as start of academic year
      return `${currentYear - 1}/${currentYear.toString().slice(-2)}`;
    }
  };

  // Helper function to get academic year from date (same logic as GroupStudentTransfersModal)
  const getAcademicYearFromDate = (dateString) => {
    if (!dateString) return "older";
    
    const date = new Date(dateString);
    
    // Academic year boundaries (August 1st)
    const aug1_2022 = new Date('2022-08-01');
    const aug1_2023 = new Date('2023-08-01');
    const aug1_2024 = new Date('2024-08-01');
    const aug1_2025 = new Date('2025-08-01');
    const aug1_2026 = new Date('2026-08-01');
    const aug1_2027 = new Date('2027-08-01');
    
    if (date >= aug1_2026 && date < aug1_2027) return "2026/27";
    if (date >= aug1_2025 && date < aug1_2026) return "2025/26";
    if (date >= aug1_2024 && date < aug1_2025) return "2024/25";
    if (date >= aug1_2023 && date < aug1_2024) return "2023/24";
    if (date >= aug1_2022 && date < aug1_2023) return "2022/23";
    return "older";
  };

  const getGradeLabelFromClass = (classItem) => {
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  useEffect(() => {
    // Set default academic year on component mount
    if (!selectedAcademicYear) {
      setSelectedAcademicYear(getDefaultAcademicYear());
    }
  }, []);

  useEffect(() => {
    if (schoolid) {
      loadTeachers();
    }
  }, [schoolid]);

  useEffect(() => {
    if (schoolid && selectedAcademicYear) {
      loadExistingClasses();
    }
  }, [schoolid, selectedAcademicYear]);

  useEffect(() => {
    parseClassNames();
  }, [rawClassText, parseMode, selectedAcademicYear]);

  const loadTeachers = async () => {
    setLoading(true);
    setError("");
    try {
      const getTeachersBySchoolId = workflowData.getTeachersBySchoolId;
      if (getTeachersBySchoolId) {
        const teachersResponse = await getTeachersBySchoolId(schoolid);
        
        const transformedTeachers = (teachersResponse || []).map(teacher => ({
          id: teacher.id,
          name: teacher.name,
          email: teacher.email,
          active: teacher.active !== false
        })).sort((a, b) => a.name.localeCompare(b.name));

        setTeachers(transformedTeachers);
      }
    } catch (err) {
      setError("Failed to load teachers. Please try again.");
      console.error("Error loading teachers:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingClasses = async () => {
    try {
      const getClassesBySchoolId = workflowData.getClassesBySchoolId;
      if (getClassesBySchoolId) {
        const classesResponse = await getClassesBySchoolId(schoolid);
        
        // Filter classes by selected academic year
        const filteredClasses = (classesResponse || []).filter(classItem => {
          const classAcademicYear = getAcademicYearFromDate(classItem.date_updated);
          return classAcademicYear === selectedAcademicYear;
        }).map(classItem => ({
          id: classItem.id,
          name: classItem.name,
          grade: getGradeLabelFromClass(classItem),
          teacher: classItem.teacher?.name || classItem.teacherName || '',
          active: classItem.active,
          trial_class: classItem.trial_class,
          date_updated: classItem.date_updated
        })).sort((a, b) => a.name.localeCompare(b.name));

        setExistingClasses(filteredClasses);
      }
    } catch (err) {
      console.error("Error loading existing classes:", err);
    }
  };

  const parseClassNames = () => {
    if (!rawClassText.trim() || !selectedAcademicYear) {
      setWorkflowData(prev => ({ ...prev, createdClasses: [] }));
      return;
    }

    let separator;
    switch (parseMode) {
      case 'comma':
        separator = ',';
        break;
      case 'tab':
        separator = '\t';
        break;
      case 'newline':
      default:
        separator = '\n';
        break;
    }

    const names = rawClassText
      .split(separator)
      .map(name => name.trim())
      .filter(name => name.length > 0)
      .map((name, index) => {
        // Auto-detect grade level from class name
        const detectedGrade = detectGradeFromName(name);
        
        // Check for duplicates with existing classes
        const isDuplicate = existingClasses.some(existing => 
          existing.name.toLowerCase() === name.toLowerCase()
        );
        
        return {
          id: `temp-${index}`,
          originalName: name,
          name: name,
          grade_level_number: detectedGrade || undefined,
          academicYear: selectedAcademicYear,
          active: true, // Always true by default
          trial_class: false,
          specialed_class: false,
          set_class: false,
          teacherId: null,
          classOwnerId: null,
          isValid: name.length > 0,
          isDuplicate: isDuplicate,
          validationError: name.length === 0 ? "Class name cannot be empty" : 
                          isDuplicate ? "Class name already exists in this academic year" : ""
        };
      });

    setWorkflowData(prev => ({ ...prev, createdClasses: names }));
  };

  const detectGradeFromName = (className) => {
    const name = className.toLowerCase();
    
    if (name.includes('junior') || name.includes('ji')) return 1;
    if (name.includes('senior') || name.includes('si')) return 2;
    if (name.includes('1st') || name.includes('first')) return 3;
    if (name.includes('2nd') || name.includes('second')) return 4;
    if (name.includes('3rd') || name.includes('third')) return 5;
    if (name.includes('4th') || name.includes('fourth')) return 6;
    if (name.includes('5th') || name.includes('fifth')) return 7;
    if (name.includes('6th') || name.includes('sixth')) return 8;
    if (name.includes('7th') || name.includes('seventh')) return 9;
    if (name.includes('8th') || name.includes('eighth')) return 10;
    
    return null;
  };

  const updateClassField = (classId, field, value) => {
    setWorkflowData(prev => ({
      ...prev,
      createdClasses: prev.createdClasses.map(classItem => {
        if (classItem.id === classId) {
          const updated = { ...classItem, [field]: value };
          
          // Update validation
          if (field === 'name') {
            const isDuplicate = existingClasses.some(existing => 
              existing.name.toLowerCase() === value.toLowerCase()
            );
            updated.isValid = value.trim().length > 0 && !isDuplicate;
            updated.isDuplicate = isDuplicate;
            updated.validationError = value.trim().length === 0 ? "Class name cannot be empty" : 
                                    isDuplicate ? "Class name already exists in this academic year" : "";
          }
          
          return updated;
        }
        return classItem;
      })
    }));
  };

  const removeClass = (classId) => {
    setWorkflowData(prev => ({
      ...prev,
      createdClasses: prev.createdClasses.filter(classItem => classItem.id !== classId)
    }));
  };

  const addManualClass = () => {
    const newClass = {
      id: `manual-${Date.now()}`,
      originalName: "",
      name: "",
      grade_level_number: undefined,
      academicYear: selectedAcademicYear,
      active: true, // Always true by default
      trial_class: false,
      specialed_class: false,
      set_class: false,
      teacherId: null,
      classOwnerId: null,
      isValid: false,
      isDuplicate: false,
      validationError: "Please enter a class name"
    };
    setWorkflowData(prev => ({
      ...prev,
      createdClasses: [...prev.createdClasses, newClass]
    }));
  };

  const filteredTeachers = teachers.filter(teacher => teacher.active);
  const validClassesCount = workflowData.createdClasses.filter(c => c.isValid && !c.isDuplicate).length;
  const duplicateClassesCount = workflowData.createdClasses.filter(c => c.isDuplicate).length;

  return (
    <div>
      <h5>Create New Classes</h5>
      <p className="text-muted">Set up the target classes for your student transitions.</p>
      
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Academic Year Selection */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-bold">Academic Year *</Form.Label>
            <Form.Select
              value={selectedAcademicYear}
              onChange={(e) => setSelectedAcademicYear(e.target.value)}
              size="lg"
            >
              <option value="">Select Academic Year</option>
              {academicYears.map(year => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Classes will be created for the {selectedAcademicYear} academic year
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6} className="d-flex align-items-end">
          {selectedAcademicYear && (
            <Button
              variant={showExistingClasses ? "outline-secondary" : "outline-info"}
              onClick={() => setShowExistingClasses(!showExistingClasses)}
              className="mb-3"
            >
              {showExistingClasses ? <FaEyeSlash className="me-1" /> : <FaEye className="me-1" />}
              {showExistingClasses ? "Hide" : "Show"} Existing Classes ({existingClasses.length})
            </Button>
          )}
        </Col>
      </Row>

      {/* Existing Classes Display */}
      <Collapse in={showExistingClasses}>
        <div className="mb-4">
          <Card>
            <Card.Header className="bg-light">
              <h6 className="mb-0">
                Existing Classes for {selectedAcademicYear}
                <Badge bg="info" className="ms-2">{existingClasses.length}</Badge>
              </h6>
            </Card.Header>
            <Card.Body>
              {existingClasses.length === 0 ? (
                <div className="text-center py-3 text-muted">
                  No existing classes found for {selectedAcademicYear}
                </div>
              ) : (
                <Row>
                  {existingClasses.map(classItem => (
                    <Col md={4} key={classItem.id} className="mb-2">
                      <div className="p-2 border rounded bg-light">
                        <div className="fw-medium">{classItem.name}</div>
                        <small className="text-muted">
                          {classItem.grade}
                          {classItem.teacher && ` • ${classItem.teacher}`}
                          {classItem.trial_class && <Badge bg="warning" className="ms-1">Trial</Badge>}
                          {!classItem.active && <Badge bg="secondary" className="ms-1">Inactive</Badge>}
                        </small>
                      </div>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {/* Only show class creation if academic year is selected */}
      {selectedAcademicYear && (
        <Row className="h-100">
          {/* Column 1: Input Text Area */}
          <Col md={4} className="border-end pe-3">
            <div className="mb-4">
              <h6 className="mb-3">Paste Class Names</h6>
              
              {/* Parse Mode Selection */}
              <Form.Group className="mb-3">
                <Form.Label>Separator Type</Form.Label>
                <Form.Select 
                  value={parseMode} 
                  onChange={(e) => setParseMode(e.target.value)}
                  size="sm"
                >
                  <option value="newline">New Line</option>
                  <option value="comma">Comma</option>
                  <option value="tab">Tab</option>
                </Form.Select>
              </Form.Group>

              {/* Text Area */}
              <Form.Group className="mb-3">
                <Form.Label>Class Names</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  placeholder={`Paste class names here...\n\nExample:\n5th Class A\nJunior Infants B\n1st Class Red\nSenior Infants Green`}
                  value={rawClassText}
                  onChange={(e) => setRawClassText(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Grade levels will be auto-detected from names
                </Form.Text>
              </Form.Group>

              <Button 
                variant="outline-primary" 
                onClick={addManualClass}
                className="w-100 mb-3"
              >
                <FaPlus className="me-1" />
                Add Single Class
              </Button>

              {workflowData.createdClasses.length > 0 && (
                <div className="text-center">
                  <Badge bg="success" className="me-1">
                    {validClassesCount} valid
                  </Badge>
                  {duplicateClassesCount > 0 && (
                    <Badge bg="warning">
                      {duplicateClassesCount} duplicates
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </Col>

          {/* Column 2: Classes Table */}
          <Col md={8}>
            <div className="mb-3">
              <h6 className="mb-3">
                Classes to Create for {selectedAcademicYear}
                <Badge bg="primary" className="ms-2">{validClassesCount}</Badge>
                {duplicateClassesCount > 0 && (
                  <Badge bg="warning" className="ms-1">{duplicateClassesCount} duplicates</Badge>
                )}
              </h6>

              {duplicateClassesCount > 0 && (
                <Alert variant="warning" className="mb-3">
                  <FaExclamationTriangle className="me-2" />
                  {duplicateClassesCount} class name(s) already exist in {selectedAcademicYear}. 
                  Please rename these classes or they won't be created.
                </Alert>
              )}

              <div style={{ height: '400px', overflowY: 'auto' }}>
                {workflowData.createdClasses.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    Paste class names in the left panel to see them here
                  </div>
                ) : (
                  <Table striped bordered hover size="sm">
                    <thead className="sticky-top bg-light">
                      <tr>
                        <th style={{ width: '22%' }}>Class Name</th>
                        <th style={{ width: '17%' }}>Grade Level</th>
                        <th style={{ width: '10%' }}>Fake</th>
                        <th style={{ width: '10%' }}>Special Ed</th>
                        <th style={{ width: '10%' }}>Set Class</th>
                        <th style={{ width: '15%' }}>Class Creator</th>
                        <th style={{ width: '15%' }}>Class Owner</th>
                        <th style={{ width: '8%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workflowData.createdClasses.map(classItem => (
                        <tr key={classItem.id} className={
                          classItem.isDuplicate ? 'table-danger' : 
                          !classItem.isValid ? 'table-warning' : ''
                        }>
                          <td>
                            <Form.Control
                              size="sm"
                              value={classItem.name}
                              onChange={(e) => updateClassField(classItem.id, 'name', e.target.value)}
                              isInvalid={!classItem.isValid || classItem.isDuplicate}
                            />
                            {(!classItem.isValid || classItem.isDuplicate) && (
                              <Form.Control.Feedback type="invalid">
                                {classItem.validationError}
                              </Form.Control.Feedback>
                            )}
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={classItem.grade_level_number || ''}
                              onChange={(e) => updateClassField(classItem.id, 'grade_level_number', e.target.value ? parseInt(e.target.value) : undefined)}
                            >
                              <option value="">Select Grade</option>
                              {gradeLevels.map(grade => (
                                <option key={grade.value} value={grade.value}>
                                  {grade.label}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={classItem.trial_class}
                              onChange={(e) => updateClassField(classItem.id, 'trial_class', e.target.checked)}
                              title="Trial class"
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={classItem.specialed_class}
                              onChange={(e) => updateClassField(classItem.id, 'specialed_class', e.target.checked)}
                              title="Special education class"
                            />
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={classItem.set_class}
                              onChange={(e) => updateClassField(classItem.id, 'set_class', e.target.checked)}
                              title="Set class"
                            />
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={classItem.teacherId || ''}
                              onChange={(e) => updateClassField(classItem.id, 'teacherId', e.target.value || null)}
                            >
                              <option value="">Select Teacher</option>
                              {filteredTeachers.map(teacher => (
                                <option key={teacher.id} value={teacher.id}>
                                  {teacher.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={classItem.classOwnerId || ''}
                              onChange={(e) => updateClassField(classItem.id, 'classOwnerId', e.target.value || null)}
                            >
                              <option value="">Select Owner</option>
                              {filteredTeachers.map(teacher => (
                                <option key={teacher.id} value={teacher.id}>
                                  {teacher.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeClass(classItem.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Show message if no academic year selected */}
      {!selectedAcademicYear && (
        <div className="text-center py-5">
          <div className="text-muted">
            <h6>Please select an academic year to continue</h6>
            <p>Choose the academic year for the classes you want to create.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateClassesStep;